import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Accordion from '@repo/ui/accordion'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import {
  CalendarTwoIcon,
  DocumentIcon,
  FamilyThreeIcon,
  LocationOneIcon,
  PdfIcon,
  UserFourIcon
} from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'

import { API, constructApiUrl } from '../../../api/apis.config'
import { getCenter } from '../../../api/centers/get-center'
import { CenterCosts } from '../../../components/pages/centers/center-costs'
import CenterCourseLocation from '../../../components/pages/centers/center-ol/center-ol-course-location'
import CenterOlDetailsSection from '../../../components/pages/centers/center-ol/center-ol-details-section'
import CenterOlListOfStudentsAndStaffs from '../../../components/pages/centers/center-ol/center-ol-list-of-students-and-staffs'
import CenterOlLocalStaff from '../../../components/pages/centers/center-ol/center-ol-local-staff'
import CenterProgramsAndActivitiesPlanner from '../../../components/pages/common/center-programs-and-activities-calendar'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import { AccomodationEnum } from '../../../lib/types/enums/accomodation-type/accomodation-type'
import { CenterStatus } from '../../../lib/types/enums/center/center-status'
import { OLTypeEnum } from '../../../lib/types/enums/ol/ol-type.enum'
import { AccordionState } from '../../../lib/types/utils'

type Props = { id: number }

export type CenterOlKey =
  | 'ol-info'
  | 'staff-and-student-info'
  | 'program'
  | 'course-location'
  | 'local-staff'

const CenterOlInformation = ({ id }: Props) => {
  const { downloadDocuments } = useDownloadDocuments()

  const { data } = useQuery({
    queryKey: ['center', id],
    queryFn: () => {
      if (id) return getCenter(id)
    },
    enabled: !!id
  })

  const showProgramDocuments =
    data?.ol && [OLTypeEnum.RESIDENCE, OLTypeEnum.PARTNER].includes(data.ol.type)

  const showProgram = showProgramDocuments || data?.activitiesStatus === CenterStatus.VALIDATED

  const initialAccordionState = useMemo<AccordionState<CenterOlKey>[]>(
    () => [
      {
        key: 'ol-info',
        label: 'Informations sur le prestataire',
        open: true,
        icon: <DocumentIcon color={tailwindColors.primary.DEFAULT} />
      },
      {
        key: 'staff-and-student-info',
        label: 'Liste des familles des jeunes et des encadrants',
        open: true,
        icon: <FamilyThreeIcon color={tailwindColors.primary.DEFAULT} />
      },
      {
        key: 'program',
        label: 'Programme',
        open: true,
        icon: <CalendarTwoIcon color={tailwindColors.primary.DEFAULT} />,
        actions: !showProgramDocuments && (
          <Button
            variant="ghost"
            icon={PdfIcon}
            label="Télécharger le programme"
            onClick={(e) => {
              e.stopPropagation()
              downloadDocuments({
                endpoint: constructApiUrl(API.centers.printCenterActivityProgram.url, { id })
              })
            }}
          />
        )
      },
      {
        key: 'course-location',
        label: 'Lieux des cours',
        open: true,
        icon: <LocationOneIcon color={tailwindColors.primary.DEFAULT} />
      },
      {
        key: 'local-staff',
        label: 'Encadrants locaux',
        open: true,
        icon: <UserFourIcon color={tailwindColors.primary.DEFAULT} />
      }
    ],
    [data, showProgramDocuments]
  )

  const [accordionState, setAccordionState] =
    useState<AccordionState<CenterOlKey>[]>(initialAccordionState)

  const renderAccordionContent = (key: CenterOlKey) => {
    if (!data) return null
    switch (key) {
      case 'ol-info':
        return data?.ol && <CenterOlDetailsSection ol={data?.ol} />
      case 'staff-and-student-info':
        return (
          data?.accomodation === AccomodationEnum.HOST_FAMILY && (
            <CenterOlListOfStudentsAndStaffs data={data} />
          )
        )
      case 'program':
        if (showProgramDocuments)
          return (
            <CenterCosts.ProgramDocuments centerId={id} mode={'readonly'} olType={data.ol?.type} />
          )
        return (
          <CenterProgramsAndActivitiesPlanner
            center={data}
            mode="view"
            currency={data?.ol?.currency}
          />
        )
      case 'course-location':
        return data?.olId && <CenterCourseLocation data={data} />

      case 'local-staff':
        return <CenterOlLocalStaff centerId={data.id} />

      default:
        return null
    }
  }

  useEffect(() => {
    setAccordionState(initialAccordionState)
  }, [initialAccordionState])

  return (
    <div className="mt-5 flex flex-col gap-4">
      {accordionState
        .filter(
          ({ key }) =>
            key !== 'staff-and-student-info' || data?.accomodation === AccomodationEnum.HOST_FAMILY
        )
        .filter(({ key }) => key !== 'program' || showProgram)
        .map(({ key, open, label, icon, actions }) => (
          <Accordion
            key={key}
            title={
              <div className="flex items-center gap-1.5">
                <Badge icon={icon} variant="primary" size="flexible" />
                <Typography variant="text" color={tailwindColors.primary.dark.DEFAULT}>
                  {label}
                </Typography>
              </div>
            }
            subtitle={actions}
            open={open}
            variant="primary"
            onToggle={() =>
              setAccordionState((prev) =>
                prev.map((item) => (item.key === key ? { ...item, open: !item.open } : item))
              )
            }
          >
            {renderAccordionContent(key)}
          </Accordion>
        ))}
    </div>
  )
}

export default CenterOlInformation
